import { memo, useMemo } from 'react';
import styled from 'styled-components';
import { CALF_RATIO } from './tracksModel';

/** The side panel of the results view (the counting-video design): mode, count, size distribution, count over time. */

const Panel = styled.aside`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 1.25rem 1.4rem;
	border-radius: 18px;
	border: 1px solid rgba(60, 60, 60, 0.9);
	background: rgba(25, 25, 25, 0.78);
	backdrop-filter: blur(14px);
	color: ${({ theme }) => theme.colors.textColor};
	min-width: 0;
`;

const Title = styled.h3`
	margin: 0;
	font-size: 1.25rem;
	font-weight: 700;
	color: #f5f5f5;
`;

export const Chips = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 0.4rem;
`;

export const Chip = styled.button`
	border: 1px solid
		${({ $active, theme }) =>
			$active ? theme.colors.darkGreen : theme.colors.backgroundAccent};
	background: ${({ $active, theme }) => ($active ? theme.colors.darkGreen : 'transparent')};
	color: ${({ $active }) => ($active ? '#fff' : '#b0b0b0')};
	border-radius: 999px;
	padding: 0.25rem 0.75rem;
	font-size: 0.82rem;
	font-weight: 600;
	cursor: pointer;
	&:hover {
		border-color: ${({ theme }) => theme.colors.green};
	}
`;

const Big = styled.div`
	font-size: 4.2rem;
	line-height: 1;
	font-weight: 800;
	color: #f5f5f5;
	font-variant-numeric: tabular-nums;
`;

const Sub = styled.div`
	color: ${({ theme }) => theme.colors.textColor};
	font-size: 0.95rem;
	margin-top: 0.35rem;
`;

const Note = styled.div`
	color: ${({ $warn }) => ($warn ? '#e0a030' : '#9a9a9a')};
	font-size: 0.78rem;
	margin-top: 0.35rem;
`;

const Rule = styled.hr`
	border: 0;
	border-top: 1px solid rgba(70, 70, 70, 0.9);
	margin: 0;
	opacity: 1;
`;

const Stats = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 0.5rem;
	b {
		display: block;
		font-size: 1.7rem;
		color: #f5f5f5;
		font-variant-numeric: tabular-nums;
	}
	span {
		color: #a0a0a0;
		font-size: 0.8rem;
	}
`;

const H = styled.div`
	font-weight: 600;
	color: #f5f5f5;
	font-size: 0.95rem;
	margin-bottom: 0.5rem;
`;

const Legend = styled.div`
	display: flex;
	gap: 1.2rem;
	font-size: 0.85rem;
	margin-top: 0.4rem;
	i {
		display: inline-block;
		width: 11px;
		height: 11px;
		margin-right: 6px;
		vertical-align: -1px;
		background: ${({ theme }) => theme.colors.green};
	}
	i.calf {
		background: ${({ theme }) => theme.colors.lightGreen};
	}
`;

const Foot = styled.div`
	margin-top: auto;
	color: #9a9a9a;
	font-size: 0.78rem;
`;

const Histogram = ({ hist }) => {
	const { counts, lo, hi } = hist;
	const peak = Math.max(1, ...counts);
	const W = 300;
	const Hh = 90;
	const bw = W / counts.length;
	const xc = ((CALF_RATIO - lo) / (hi - lo)) * W;
	return (
		<svg
			viewBox={`0 0 ${W} ${Hh + 16}`}
			width="100%"
			role="img"
			aria-label="Body size distribution">
			{counts.map((c, i) => {
				const h = (Hh * c) / peak;
				const mid = lo + (i + 0.5) * hist.width;
				return c ? (
					<rect
						key={i}
						x={i * bw + 1.5}
						y={Hh - h}
						width={bw - 3}
						height={h}
						fill={mid < CALF_RATIO ? '#90d7ad' : '#20AC6B'}
					/>
				) : null;
			})}
			<line
				x1={xc}
				x2={xc}
				y1={0}
				y2={Hh}
				stroke="#b0b0b0"
				strokeDasharray="3 4"
				strokeWidth="1"
			/>
			<line x1={0} x2={W} y1={Hh} y2={Hh} stroke="#5a5a5a" strokeWidth="1" />
			<text x={0} y={Hh + 13} fill="#9a9a9a" fontSize="10">
				smaller
			</text>
			<text x={W} y={Hh + 13} fill="#9a9a9a" fontSize="10" textAnchor="end">
				larger
			</text>
		</svg>
	);
};

const Sparkline = ({ series, frame }) => {
	const W = 300;
	const Hh = 70;
	const n = series?.length || 0;
	const path = useMemo(() => {
		if (!n) return null;
		const max = Math.max(1, ...series);
		const step = Math.max(1, Math.floor(n / 300));
		const pts = [];
		for (let i = 0; i < n; i += step)
			pts.push([(i / Math.max(1, n - 1)) * W, Hh - (Hh * series[i]) / max]);
		pts.push([W, Hh - (Hh * series[n - 1]) / max]);
		return { line: pts.map(p => p.join(',')).join(' '), max };
	}, [series, n]);
	if (!path) return null;
	const x = (frame / Math.max(1, n - 1)) * W;
	return (
		<svg viewBox={`0 0 ${W} ${Hh + 2}`} width="100%" role="img" aria-label="Count over time">
			<defs>
				<clipPath id="played">
					<rect x="0" y="0" width={x} height={Hh + 2} />
				</clipPath>
			</defs>
			<polyline points={path.line} fill="none" stroke="#3b4a42" strokeWidth="2" />
			<g clipPath="url(#played)">
				<polygon points={`0,${Hh} ${path.line} ${W},${Hh}`} fill="#18462f" />
				<polyline points={path.line} fill="none" stroke="#20AC6B" strokeWidth="2.5" />
			</g>
			<line x1={x} x2={x} y1={0} y2={Hh} stroke="#f5f5f5" strokeWidth="1" opacity="0.6" />
			<line x1={0} x2={W} y1={Hh} y2={Hh} stroke="#5a5a5a" strokeWidth="1" />
		</svg>
	);
};

const SliderRow = styled.label`
	display: flex;
	flex-direction: column;
	gap: 0.35rem;
	margin: 0;
	font-size: 0.82rem;
	color: #f5f5f5;
	font-weight: 600;
	input {
		width: 100%;
		accent-color: ${({ theme }) => theme.colors.green};
	}
`;

/** Confidence slider: hides animals whose best detection is below the value; the stored count is the lowest value. */
const Sensitivity = ({ value, min, onChange }) => (
	<SliderRow>
		<span style={{ display: 'flex', justifyContent: 'space-between' }}>
			Sensitivity
			<span style={{ fontVariantNumeric: 'tabular-nums' }}>{Math.round(value * 100)} %</span>
		</span>
		<input
			type="range"
			min={min}
			max={0.95}
			step={0.01}
			value={value}
			onChange={e => onChange(Number(e.target.value))}
			aria-label="Minimum detection confidence"
			aria-valuetext={`${Math.round(value * 100)} percent`}
		/>
		<span
			style={{
				display: 'flex',
				justifyContent: 'space-between',
				fontWeight: 400,
				fontSize: '0.72rem',
				color: '#9a9a9a',
			}}>
			<span>Find more</span>
			<span>Fewer false alarms</span>
		</span>
	</SliderRow>
);

const MODE_LABEL = { hover: 'Total count', line: 'Line crossing', max: 'Most in view' };

const CountPanel = ({
	modes,
	mode,
	onModeChange,
	count,
	subtitle,
	note,
	noteWarn,
	inView,
	maxInView,
	hist,
	series,
	frame,
	modelLabel,
	sensitivity,
}) => (
	<Panel>
		<Title>Reindeer count</Title>
		<Chips role="tablist" aria-label="Counting mode">
			{modes.map(m => (
				<Chip
					key={m}
					type="button"
					role="tab"
					aria-selected={m === mode}
					$active={m === mode}
					onClick={() => onModeChange(m)}>
					{MODE_LABEL[m]}
				</Chip>
			))}
		</Chips>
		<div>
			<Big>{count ?? '—'}</Big>
			<Sub>{subtitle}</Sub>
			{note && <Note $warn={noteWarn}>{note}</Note>}
		</div>
		{sensitivity?.available && (
			<>
				<Rule />
				<Sensitivity {...sensitivity} />
			</>
		)}
		<Rule />
		<Stats>
			<div>
				<b>{inView}</b>
				<span>In view now</span>
			</div>
			<div>
				<b>{maxInView}</b>
				<span>Most in view at once</span>
			</div>
		</Stats>
		<Rule />
		<div>
			<H>Body size of counted animals</H>
			<Histogram hist={hist} />
			<Legend>
				<span>
					<i className="calf" />
					{hist.calf} calf-sized
				</span>
				<span>
					<i />
					{hist.counts.reduce((a, b) => a + b, 0) - hist.calf} adult-sized
				</span>
			</Legend>
			<Note>Size relative to the herd at the same distance (estimate)</Note>
		</div>
		<Rule />
		<div>
			<H>Count over time</H>
			<Sparkline series={series} frame={frame} />
		</div>
		<Foot>{modelLabel}</Foot>
	</Panel>
);

export default memo(CountPanel);
